import { useState } from 'react';

import { DatePicker } from '@/components/DatePicker';
import { Field, controlClass } from '@/components/Field';
import { Button, Card, Notice } from '@/components/PageHeading';
import { pickEventDay, type BookingDraft, type DraftProblem } from '@core/bookings/draft';
import { eventTitle, withEventDays } from '@core/bookings/newBookingSteps';
import { chargeableDays } from '@core/bookings/pricing';
import { monthGrid, shiftMonth, startOfMonth } from '@core/calendar/calendar';
import { toDayjs, toIsoDate } from '@core/format/dates';

const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** The occasions a clerk types most, offered as one click. Any other is typed. */
const occasions = ['Wedding', 'Reception', 'Engagement', 'Sangeet'];

/**
 * Step 1 of a new booking on the website (canvas "Booking flow", 2026-10-26), as on the phone:
 * **one question — the event day, or days.** A click is the event; a later click makes it an event
 * over several days (the core's `pickEventDay`), or the 1 · 2 · 3 days beside (`withEventDays`).
 * Collect and return follow from the shop's grace either side and the charged days are the event
 * days, read back beside the month with Change for the booking that needs them moved.
 *
 * <p>
 * Each day on the month says what it is — event, collect, return, out — so the date pairs the
 * model keeps apart are seen at once rather than typed as five fields. Monday-first, as the calendar
 * is. A bar at the foot reads the choice back and goes on.
 * </p>
 */
export function WhenStep({
  draft,
  today,
  problems,
  busy,
  onChange,
  onNext,
}: {
  draft: BookingDraft;
  today: string;
  problems: readonly DraftProblem[];
  busy: boolean;
  onChange: (next: BookingDraft) => void;
  onNext: () => void;
}) {
  const [month, setMonth] = useState(() => startOfMonth(draft.eventDate || today));
  const [changing, setChanging] = useState(false);
  const patch = (changes: Partial<BookingDraft>) => onChange({ ...draft, ...changes });
  const days = chargeableDays(draft.chargeFrom, draft.chargeTo);
  const hasDates = draft.eventDate !== '';
  const eventDays = hasDates ? days : 0;

  // What a day on the month is to this booking: the event, the collect or return day, or out.
  const tagOf = (iso: string) => {
    if (!hasDates || iso < draft.possessionFrom || iso > draft.possessionTo) {
      return '';
    }

    if (iso >= draft.chargeFrom && iso <= draft.chargeTo) {
      return 'Event';
    }

    if (iso === draft.possessionFrom) {
      return 'Collect';
    }

    return iso === draft.possessionTo ? 'Return' : 'Out';
  };

  return (
    <div className="mx-auto w-full max-w-[1232px]">
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-7 xl:col-span-8">
          <Card>
            <div className="mb-4 flex items-center justify-between">
              <button
                type="button"
                aria-label="Previous month"
                onClick={() => setMonth(shiftMonth(month, -1))}
                className="border-border-color flex size-10 cursor-pointer items-center justify-center rounded-lg border bg-white hover:bg-light"
              >
                <i className="icon icon-chevron-left" />
              </button>
              <p className="mb-0 font-serif text-[22px] font-semibold text-gray-900">
                {toDayjs(month)?.format('MMMM YYYY')}
              </p>
              <button
                type="button"
                aria-label="Next month"
                onClick={() => setMonth(shiftMonth(month, 1))}
                className="border-border-color flex size-10 cursor-pointer items-center justify-center rounded-lg border bg-white hover:bg-light"
              >
                <i className="icon icon-chevron-right" />
              </button>
            </div>

            <div className="text-default grid grid-cols-7 gap-1.5 text-center text-[12px] font-semibold">
              {weekdays.map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>

            <div className="mt-1.5 grid grid-cols-7 gap-1.5">
              {monthGrid(month, today)
                .flat()
                .map((cell) => {
                  const tag = tagOf(cell.iso);
                  const charged = tag === 'Event';
                  const out = tag !== '' && !charged;
                  const past = cell.iso < today;

                  return (
                    <button
                      key={cell.iso}
                      type="button"
                      disabled={busy || past}
                      aria-pressed={charged}
                      aria-label={`${toDayjs(cell.iso)?.format('dddd D MMMM') ?? cell.iso}${
                        tag ? `, ${tag.toLowerCase()}` : ''
                      }`}
                      onClick={() => patch(pickEventDay(draft, cell.iso))}
                      className={`flex h-16 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-[10px] disabled:cursor-not-allowed max-sm:h-12 ${
                        charged
                          ? 'bg-primary text-white'
                          : out
                            ? 'bg-primary/15 text-gray-900'
                            : cell.isToday
                              ? 'border-primary border-[1.5px] bg-white text-gray-900'
                              : 'border-border-color border bg-white text-gray-900 hover:bg-light'
                      } ${!cell.inMonth || past ? 'opacity-40' : ''}`}
                    >
                      <span className="font-mono-nums text-[16px] font-semibold">{cell.day}</span>
                      {tag && (
                        <span className="text-[10px] font-semibold tracking-wide uppercase max-sm:hidden">
                          {tag}
                        </span>
                      )}
                    </button>
                  );
                })}
            </div>

            <div className="text-default mt-4 flex flex-wrap gap-4 text-[12px]">
              <Key className="bg-primary" label="The event — the days charged" />
              <Key className="bg-primary/15" label="Out with the customer" />
            </div>
            <p className="text-default mt-2 mb-0 text-[12px]">
              {hasDates
                ? 'Click a later day if the event runs over several, or choose the days beside.'
                : 'Click the day of the event.'}
            </p>
          </Card>
        </div>

        <div className="col-span-12 flex flex-col gap-4 lg:col-span-5 xl:col-span-4">
          {problems.map((problem) => (
            <Notice key={problem.message} tone="warning" title={problem.message} />
          ))}

          <Card>
            <p className="mb-3 text-[15px] font-bold text-gray-900">The event</p>

            <Field label="Occasion">
              <input
                value={draft.eventKind}
                disabled={busy}
                onChange={(event) => patch({ eventKind: event.target.value })}
                placeholder="Wedding, reception, engagement…"
                className={controlClass}
              />
            </Field>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {occasions.map((occasion) => (
                <button
                  key={occasion}
                  type="button"
                  disabled={busy}
                  aria-pressed={draft.eventKind === occasion}
                  onClick={() => patch({ eventKind: occasion })}
                  className={`h-[34px] cursor-pointer rounded-full border px-3.5 text-[13px] ${
                    draft.eventKind === occasion
                      ? 'border-primary bg-primary font-medium text-white'
                      : 'border-border-color bg-white text-gray-900 hover:bg-light'
                  }`}
                >
                  {occasion}
                </button>
              ))}
            </div>

            <div className="mt-4">
              <p className="mb-1.5 text-sm font-semibold text-gray-900">How many days of event</p>
              <div className="border-border-color inline-flex rounded-lg border p-0.5">
                {[1, 2, 3].map((count) => (
                  <button
                    key={count}
                    type="button"
                    disabled={busy || !hasDates}
                    aria-pressed={eventDays === count}
                    onClick={() => patch(withEventDays(draft, count))}
                    className={`h-8 cursor-pointer rounded-md px-3.5 text-[13px] disabled:cursor-not-allowed disabled:opacity-50 ${
                      eventDays === count
                        ? 'bg-primary-transparent text-primary font-semibold'
                        : 'text-gray-900 hover:bg-light'
                    }`}
                  >
                    {count} {count === 1 ? 'day' : 'days'}
                  </button>
                ))}
              </div>
            </div>

            {hasDates ? (
              <>
                <ul className="border-border-color mt-4 mb-0 flex list-none flex-col border-t ps-0 pt-1">
                  <Fact
                    label="Collects"
                    value={toDayjs(draft.possessionFrom)?.format('ddd D MMM')}
                  />
                  <Fact label="Returns" value={toDayjs(draft.possessionTo)?.format('ddd D MMM')} />
                  <Fact
                    label="Charged"
                    value={`${days} ${days === 1 ? 'day' : 'days'} · ${
                      toDayjs(draft.chargeFrom)?.format('D MMM') ?? ''
                    }${
                      draft.chargeTo !== draft.chargeFrom
                        ? ` – ${toDayjs(draft.chargeTo)?.format('D MMM') ?? ''}`
                        : ''
                    }`}
                  />
                </ul>

                {changing && (
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <DateField
                      label="Collects on"
                      value={draft.possessionFrom}
                      disabled={busy}
                      onChange={(possessionFrom) => patch({ possessionFrom })}
                    />
                    <DateField
                      label="Brings back by"
                      value={draft.possessionTo}
                      disabled={busy}
                      onChange={(possessionTo) => patch({ possessionTo })}
                    />
                    <DateField
                      label="Charged from"
                      value={draft.chargeFrom}
                      disabled={busy}
                      onChange={(chargeFrom) => patch({ chargeFrom })}
                    />
                    <DateField
                      label="Charged to"
                      value={draft.chargeTo}
                      disabled={busy}
                      onChange={(chargeTo) => patch({ chargeTo })}
                    />
                    <p className="text-default col-span-2 mb-0 text-[12px]">
                      The charged days are what the customer pays for; collect and return are when
                      the garments are out, and what the calendar holds.
                    </p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setChanging((was) => !was)}
                  aria-expanded={changing}
                  className="text-primary mt-2 cursor-pointer text-[13px] font-semibold"
                >
                  {changing ? 'Done' : 'Change the collect and return days'}
                </button>
              </>
            ) : (
              <p className="text-default mt-4 mb-0 text-[13px]">
                Collect, return and the days charged fill in once the day is picked.
              </p>
            )}
          </Card>

          <Card>
            <p className="mb-1 text-[15px] font-bold text-gray-900">Who is booking?</p>
            <p className="text-default mb-0 text-[13px]">
              Asked at the bag, after they have seen what is free. Nobody has to give a name to see
              what fits.
            </p>
          </Card>
        </div>
      </div>

      <div className="sticky bottom-4 z-10 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-[14px] bg-white px-4 py-3 shadow-[0_4px_24px_rgb(15_23_42/0.14)]">
        <span className="font-semibold text-gray-900">
          {hasDates ? eventTitle(draft) : 'Pick the day of the event to go on.'}
          {hasDates && (
            <span className="text-default font-normal">
              {' · out '}
              {toDayjs(draft.possessionFrom)?.format('ddd D MMM')} –{' '}
              {toDayjs(draft.possessionTo)?.format('ddd D MMM')}
            </span>
          )}
        </span>
        <Button
          variant="primary"
          data-enter-key-commit
          disabled={!hasDates || problems.length > 0}
          onClick={onNext}
          className="h-11 justify-center px-5 text-[15px]"
        >
          Pick the looks
          <i className="icon icon-arrow-right" />
        </Button>
      </div>
    </div>
  );
}

function Key({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <i className={`inline-block size-3 rounded-sm ${className}`} />
      {label}
    </span>
  );
}

function Fact({ label, value }: { label: string; value: string | undefined }) {
  return (
    <li className="border-border-color flex list-none items-center justify-between border-t py-2 text-[13px] first:border-t-0">
      <span className="text-default">{label}</span>
      <b className="text-gray-900">{value ?? '—'}</b>
    </li>
  );
}

function DateField({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <Field label={label}>
      <DatePicker
        className="w-full"
        value={toDayjs(value) ?? null}
        disabled={disabled}
        onChange={(picked) => onChange(toIsoDate(picked) ?? '')}
      />
    </Field>
  );
}
